// Pure presentation helpers of the unified Requests page.
// Nothing here decides what a user may do: that comes from the API's
// `actions` (and every action is checked again by its own backend endpoint).

import { integrationLabel } from "../../../../utils/integrations";
import { pick } from "./unifiedText";

export const formatDateTime = (value, lang) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(lang === "ar" ? "ar-SA" : "en-GB", { dateStyle: "medium", timeStyle: "short" });
};
export const formatDate = (value, lang) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB");
};

// "Amal · am". Only what the record holds; nothing is invented.
export const actorText = (actor) => (actor ? [actor.name, actor.role].filter(Boolean).join(" · ") : "");

export const kindText = (item, lang) => pick(item?.kind?.label, lang) || item?.kind?.code || "";

export const storeName = (storeId, stores, lang) => {
  const found = (stores || []).find((store) => store.id === storeId);
  return (found && (lang === "ar" ? found.nameAr : found.nameEn || found.nameAr)) || storeId || "";
};

export const branchName = (branchId, branches, lang) => {
  if (!branchId) return "";
  const found = (branches || []).find((branch) => branch.id === branchId);
  return (found && (lang === "ar" ? found.nameAr : found.nameEn || found.nameAr)) || `…${String(branchId).slice(-4)}`;
};

export const appName = (application, lang) => (application ? integrationLabel(application, lang) || application : "");

// Labels of the actions the API says the viewer could take (for list rows).
export function actionLabels(item, t) {
  const a = item?.actions || {};
  const labels = [];
  switch (item?.identity?.source) {
    case "operations":
      if (a.approveAsClient) labels.push(t.action.approveClient);
      if (a.approveAsManager) labels.push(t.action.approveManager);
      if (a.approveOnBehalf) labels.push(t.action.onBehalf);
      (a.changeStatusTo || []).forEach((status) => labels.push(t.action[{ active: "activate", completed: "completed", rejected: "rejected", canceled: "cancelCampaign", inprogress: "inprogress" }[status]] || status));
      if (a.cancel && !(a.changeStatusTo || []).includes("canceled")) labels.push(t.action.cancelCampaign);
      if (a.deleteRequest) labels.push(t.action.deleteRequest);
      break;
    case "dashboard":
      if (a.review) labels.push(t.action.approve, t.action.reject);
      if (a.cancel) labels.push(t.action.cancel);
      if ((a.uploadKinds || []).length) labels.push(t.action.upload);
      break;
    case "appChange":
      if (a.review) labels.push(t.action.approve, t.action.reject);
      if (a.cancel) labels.push(t.action.cancel);
      break;
    case "appSubscription":
      if (a.send) labels.push(item.status?.native === "failed" ? t.action.retry : t.action.send);
      if (a.cancel) labels.push(t.action.cancel);
      if (a.complete) labels.push(t.action.complete);
      break;
    default:
  }
  return [...new Set(labels)];
}

// ---- proposed changes (Dashboard requests): current value -> requested value -----------

const SENSITIVE = "[redacted]";
const DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const shift = (range) => (range && range.from && range.to ? `${range.from}–${range.to}` : "");

function hoursOfRequest(workTime, t) {
  const days = workTime?.[0]?.days || {};
  return Object.fromEntries(
    DAYS.filter((day) => days[day]).map((day) => {
      const text = [shift(days[day].morning), shift(days[day].evening)].filter(Boolean).join(" / ");
      return [day, text || t.fields.closed];
    }),
  );
}

function hoursOfBranch(branch, t) {
  // normalized rows of the dashboard hook: [{ closed, from1, to1, from2, to2 }] Sunday..Saturday
  const rows = branch?.hours;
  if (!Array.isArray(rows)) return null;
  return Object.fromEntries(
    DAYS.map((day, index) => {
      const row = rows[index];
      if (!row) return [day, null];
      const text = row.closed ? t.fields.closed : [row.from1 && row.to1 ? `${row.from1}–${row.to1}` : "", row.from2 && row.to2 ? `${row.from2}–${row.to2}` : ""].filter(Boolean).join(" / ");
      return [day, text || t.fields.closed];
    }),
  );
}

const display = (value, t) => {
  if (value === SENSITIVE) return t.hidden;
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

// -> [{ label, current, requested, secret? }]. `current` is only filled when
// the dashboard already holds that restaurant's data; otherwise it is omitted
// (never guessed). Secrets are shown as "will be changed", never as values.
export function changeRows(request, context, t) {
  const changes = request?.details?.changes;
  if (!changes || typeof changes !== "object" || changes.truncated || changes.unreadable) return [];
  const action = request.details.action;
  const sameStore = context?.storeId && context.storeId === request.scope?.storeId;
  const branch = sameStore ? (context.branches || []).find((item) => item.id === (changes.branchId || request.scope?.branchId)) : null;
  const rows = [];
  const push = (label, requested, current) => rows.push({ label, requested: display(requested, t), current: display(current, t) });

  switch (action) {
    case "updateBranchHours": {
      const requested = hoursOfRequest(changes.workTime, t);
      const current = hoursOfBranch(branch, t);
      DAYS.filter((day) => requested[day] !== undefined).forEach((day) => push(t.fields.day[day], requested[day], current?.[day]));
      return rows;
    }
    case "maintenanceClosure":
      push(t.fields.startDate, changes.from, branch?.maintenance?.from ? String(branch.maintenance.from).slice(0, 10) : "");
      push(t.fields.endDate, changes.to, branch?.maintenance?.to ? String(branch.maintenance.to).slice(0, 10) : "");
      if (changes.reason) push(t.fields.reason, changes.reason, branch?.maintenance?.reason);
      return rows;
    case "updateBranch": {
      const known = { nameAr: branch?.nameAr, name: branch?.nameAr, phone: branch?.phone, cityAr: branch?.cityAr, city: branch?.cityAr, locationUrl: branch?.locationUrl, location: branch?.locationUrl, coordination: branch?.coordination };
      Object.entries(changes.changes || {}).forEach(([key, value]) => push(key, value, known[key]));
      return rows;
    }
    case "updateBranchStatus":
      push("status", changes.status, branch?.status);
      return rows;
    case "updateBranchIntegrationStatus":
      push("integrationStatus", changes.status, branch?.integrationStatus);
      return rows;
    case "updateAccount": {
      const account = sameStore ? (context.accounts || []).find((item) => item.id === changes.id) : null;
      const known = { app: account?.app, email: account?.email, id: account?.accountId, status: account?.status, platformUrl: account?.platformUrl };
      Object.entries(changes.changes || {}).forEach(([key, value]) => {
        if (key === "pass") rows.push({ label: key, requested: t.fields.willChange, current: "", secret: true });
        else push(key, value, known[key]);
      });
      return rows;
    }
    case "updatePricing": {
      const current = sameStore ? context.pricing || {} : {};
      Object.entries(changes.pricing || {}).forEach(([key, value]) => push(key, value, current[key]));
      return rows;
    }
    default:
  }

  // everything else: the requested values, flattened (nothing to compare with)
  const walk = (value, prefix) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      Object.entries(value).forEach(([key, item]) => walk(item, prefix ? `${prefix}.${key}` : key));
    } else if (Array.isArray(value) && value.every((item) => item === null || typeof item !== "object")) {
      push(prefix, value.join(", "));
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => walk(item, `${prefix}[${index}]`));
    } else if (value !== undefined && value !== null && value !== "") {
      const secret = value === SENSITIVE;
      if (secret) rows.push({ label: prefix, requested: t.fields.willChange, current: "", secret: true });
      else push(prefix, value);
    }
  };
  walk(changes, "");
  return rows;
}
